import clsx from 'clsx';
import type * as React from 'react';
import type { PressEvent } from 'react-aria';

import { AtomButton } from '../Button';
import { Disclosure, DisclosureChevron, DisclosurePanel, DisclosureTrigger } from '../Disclosure';
import { type IconDefinition, renderIcon } from '../util';

/**
 * The column every part of a chat transcript stands in: a message from either side, what is still
 * on its way, the suggestions at the end.
 *
 * One 16px gutter for all of them is what lines the conversation up. Blocks keep only the padding
 * intrinsic to their own surface, so bubbles, paragraphs, cards and hover fills share an outer
 * edge. Differs from a plain `flex-col` only by fixing that gutter, which is the point: it is not a
 * value to choose per surface.
 */
export function ChatColumn(props: {
    className?: string;
    children: React.ReactNode;
    ref?: React.Ref<HTMLDivElement>;
}) {
    const { className, children, ref } = props;

    return (
        <div ref={ref} className={clsx('flex flex-col px-4', className)}>
            {children}
        </div>
    );
}

/**
 * What the user said, as a bubble against the right edge of a {@link ChatColumn}.
 *
 * One shape for a message whatever its state — sent, still on its way, or held back until the other
 * side is ready — with `dimmed` for the ones not settled yet, so the bubble can be swapped for the
 * sent one in place. Whatever goes under it, right-aligned with it, is the `footer`: controls that
 * reveal themselves on hover through the bubble's `group/message`, or a {@link ChatLine} saying how
 * the send is going. Differs from {@link Card}, which frames content of its own rather than a turn
 * in a conversation.
 */
export function ChatBubble(props: {
    /** For a message the other side has not taken yet: the bubble is there, but not settled. */
    dimmed?: boolean;
    /** Controls or status under the bubble. */
    footer?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
    ref?: React.Ref<HTMLDivElement>;
}) {
    const { dimmed = false, footer, className, children, ref } = props;

    return (
        // Room kept on the left so the bubble reads as the user's side, however long the message;
        // the column owns the right inset shared with every other transcript item.
        <div
            ref={ref}
            className={clsx('group/message flex flex-col items-end gap-1 pl-8', className)}
        >
            <div
                className={clsx(
                    'flex max-w-full min-w-0 flex-col gap-2 rounded-xl bg-neutral-ui px-3 py-2 text-ui-base text-neutral-high transition-opacity select-text',
                    dimmed && 'opacity-70'
                )}
            >
                {children}
            </div>
            {footer}
        </div>
    );
}

/**
 * How a line reads: something that was done, or something that went wrong.
 */
export type ChatLineTone = 'default' | 'warning';

export type ChatLineProps = {
    /**
     * Drawn in a fixed slot the width of a text line, so every label starts at the same place
     * whatever glyph is drawn. Left out for a line under a {@link ChatDisclosure}, whose summary
     * has drawn it.
     */
    icon?: IconDefinition;
    /** What the line says. Wraps onto further lines; the icon stays on the first. */
    children: React.ReactNode;
    /**
     * Set right after the label, never wrapping: a {@link ChatLineStatus}, a chevron, a way in.
     * Small buttons fit too — the slot is a text line tall and lets them overflow it.
     */
    trailing?: React.ReactNode;
    /** Makes the whole line the control, not a word in it. */
    onPress?: (event: PressEvent) => void;
    /** Sweeps a sheen across the label while what it reports is still happening. */
    loading?: boolean;
    /** @default 'default' */
    tone?: ChatLineTone;
    className?: string;
    ref?: React.Ref<HTMLElement>;
    id?: string;
    'aria-expanded'?: React.AriaAttributes['aria-expanded'];
    'aria-controls'?: React.AriaAttributes['aria-controls'];
    'aria-label'?: string;
};

/**
 * One line of what happened in a chat, set in a {@link ChatColumn}: a tool the agent ran, a wait, a
 * warning, how a send is going.
 *
 * Every such line is built on this, which is what keeps them aligned: the same icon slot, text,
 * height and padding — and, given `onPress`, the same control, pressable over its whole width
 * rather than through a button set in the text. Differs from {@link Status}, a pill that stands on
 * its own, and from {@link ListItem}, a row of a list with selection and keyboard navigation: a
 * line is a sentence in a transcript, muted and small, read in order with the text around it.
 */
export function ChatLine(props: ChatLineProps) {
    const {
        icon,
        children,
        trailing,
        onPress,
        loading = false,
        tone = 'default',
        className: extraClassName,
        ref,
        ...rest
    } = props;

    const className = clsx(
        // `items-start` with a slot the height of a text line, so an icon stays on the first line
        // of a label that wraps rather than drifting to the middle of it. A hair more than a
        // button's padding above and below, so stacked lines breathe without reading as a list.
        'flex items-start gap-2 rounded-md px-2 py-1.25 text-ui-small',
        tone === 'warning' ? 'text-danger-standout' : 'text-muted',
        onPress &&
            'cursor-pointer text-left outline-hidden transition-colors hover:bg-(--neutral-a3) hover:text-base data-focus-visible:focus-ring data-[pressed=true]:bg-(--neutral-a4)',
        extraClassName
    );
    const content = (
        <>
            {icon ? (
                <span className="flex size-4 shrink-0 items-center justify-center">
                    {renderIcon(icon, { className: 'size-3.5' })}
                </span>
            ) : null}
            <span className={clsx('min-w-0', loading && 'shimmer-text')}>{children}</span>
            {trailing ? (
                <span className="flex h-4 shrink-0 items-center gap-1.5">{trailing}</span>
            ) : null}
        </>
    );

    if (onPress) {
        return (
            <AtomButton
                ref={ref as React.Ref<HTMLButtonElement>}
                {...rest}
                aria-busy={loading || undefined}
                className={className}
                onPress={onPress}
            >
                {content}
            </AtomButton>
        );
    }

    return (
        <div
            ref={ref as React.Ref<HTMLDivElement>}
            {...rest}
            aria-busy={loading || undefined}
            className={className}
        >
            {content}
        </div>
    );
}

/**
 * What a status says about the thing a line reports: still going, finished, failed, or given up.
 */
export type ChatLineStatusKind = 'working' | 'success' | 'danger' | 'neutral';

/**
 * A status set against a {@link ChatLine}'s label, for its `trailing` slot: a dot in the status's
 * colour and a word for it. Differs from {@link Status} by having no pill of its own — it is read
 * as part of the line.
 */
export function ChatLineStatus(props: {
    kind: ChatLineStatusKind;
    children: React.ReactNode;
    /** Replaces the dot colour the `kind` sets, e.g. `bg-primary-solid`. */
    dotClassName?: string;
}) {
    const { kind, children, dotClassName } = props;

    return (
        <span
            className={clsx(
                'inline-flex items-center gap-1.5 whitespace-nowrap',
                STATUS_TEXT_CLASSNAME[kind]
            )}
        >
            <span
                aria-hidden
                className={clsx(
                    'size-1.5 shrink-0 rounded-full',
                    dotClassName ?? STATUS_DOT_CLASSNAME[kind]
                )}
            />
            <span className={kind === 'working' ? 'shimmer-text' : undefined}>{children}</span>
        </span>
    );
}

/** Dot colour per {@link ChatLineStatusKind}. */
const STATUS_DOT_CLASSNAME: Record<ChatLineStatusKind, string> = {
    working: 'bg-info-solid',
    success: 'bg-success-solid',
    danger: 'bg-danger-solid',
    neutral: 'bg-neutral-solid',
};

/** Label colour per {@link ChatLineStatusKind}; `working` is drawn by the sheen instead. */
const STATUS_TEXT_CLASSNAME: Record<ChatLineStatusKind, string> = {
    working: 'text-muted',
    success: 'text-success',
    danger: 'text-danger-standout',
    neutral: 'text-muted',
};

/**
 * A {@link ChatLine} that opens onto more: the reasoning behind a step, or several lines of the
 * same thing behind one that sums them up.
 *
 * The line is the trigger, pressable over its whole width, with the chevron set after the label.
 * What it opens hangs under it from a rail drawn down from the icon, indented so a {@link ChatLine}
 * inside starts where the label above it does. Differs from {@link Disclosure}, which it is built
 * on, by fixing the trigger and the indentation to the transcript's grid.
 */
export function ChatDisclosure(props: {
    icon?: IconDefinition;
    label: React.ReactNode;
    /** Sweeps a sheen across the label while what it sums up is still happening. */
    loading?: boolean;
    defaultExpanded?: boolean;
    children: React.ReactNode;
}) {
    const { icon, label, loading = false, defaultExpanded, children } = props;

    return (
        <Disclosure defaultExpanded={defaultExpanded}>
            <DisclosureTrigger>
                <ChatLine
                    icon={icon}
                    loading={loading}
                    trailing={<DisclosureChevron className="size-3" />}
                >
                    {label}
                </ChatLine>
            </DisclosureTrigger>
            <DisclosurePanel>
                {/* The rail sits at the centre of the icon slot above, and the inset is that slot
                    plus the line's gap — so a line in here, with its own padding, puts its text
                    under the label. */}
                <div className="relative pl-6 before:absolute before:inset-y-0 before:left-4 before:w-px before:bg-neutral-subtle before:content-['']">
                    {children}
                </div>
            </DisclosurePanel>
        </Disclosure>
    );
}
